import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Cap } from '@artmozaik/shared';
import { api, ApiError, tokens, type Me } from './api';

type AuthState = {
  me: Me | null;
  loading: boolean;
  /** Açılışta sunucuya ulaşılamadı (çevrimdışı / sunucu yeniden başlıyor): oturum SİLİNMEZ, yeniden denenir. */
  baglantiYok: boolean;
  yenidenDene: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (cap: Cap) => boolean;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [baglantiYok, setBaglantiYok] = useState(false);

  /**
   * Açılışta elde token varsa kimliği doğrula; yoksa giriş ekranına düş.
   * Jeton YALNIZ sunucu "geçersiz" derse (401/403) silinir. Ağ hatası ya da sunucunun
   * kısa süre yanıt verememesi (dağıtım sırasında 502) oturumu düşürmez: kurulu uygulama
   * çevrimdışı açıldığında kullanıcı yeniden parola yazmak zorunda kalmaz.
   */
  const kimlikOku = useCallback(() => {
    if (!tokens.access && !tokens.refresh) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api<Me>('/api/me')
      .then((m) => { setMe(m); setBaglantiYok(false); })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) { tokens.clear(); setBaglantiYok(false); }
        else setBaglantiYok(true);
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(kimlikOku, [kimlikOku]);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api<{ user: Me; accessToken: string; refreshToken: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    tokens.set(r.accessToken, r.refreshToken);
    setMe(r.user);
  }, []);

  const logout = useCallback(async () => {
    await api('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: tokens.refresh }),
    }).catch(() => undefined);
    tokens.clear();
    // Uygulama durumu da temizlenir (kaydırma konumları, son sayfa); servis çalışanının kabuk önbelleği kişisel veri taşımaz, kalır.
    const sil = () => { try { sessionStorage.clear(); } catch { /* özel pencere */ } };
    sil();
    setMe(null);
    // Kabuk kapanırken son kaydırma konumunu yazar; o da silinsin diye bir tur sonra yeniden.
    window.setTimeout(sil, 0);
  }, []);

  /** Yetki kontrolü — API'deki rol matrisinin aynısı, sunucudan geliyor. */
  const can = useCallback((cap: Cap) => Boolean(me?.caps.includes(cap)), [me]);

  const value = useMemo(() => ({ me, loading, baglantiYok, yenidenDene: kimlikOku, login, logout, can }), [me, loading, baglantiYok, kimlikOku, login, logout, can]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth, AuthProvider dışında kullanıldı');
  return v;
}
